import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Audit, CorrectiveAction, Finding } from "@/app/pages/auditcentre";
import { SingleUserCombobox } from "../ui/SingleUserCombobox";
import { EntityLinkedDocumentsSection } from './EntityLinkedDocumentsSection';

interface CorrectiveActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action?: CorrectiveAction | null;
  onSave: (data: Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  findings?: Finding[];
  // DED-1448: Add audits prop for linking to audits
  audits?: Audit[];
  isSubmitting?: boolean;
  // DED-973: Controls to inherit from parent entity
  inheritedControls?: string[];
}

export function CorrectiveActionDialog({
  open,
  onOpenChange,
  action,
  onSave,
  findings = [],
  audits = [],
  isSubmitting = false,
  inheritedControls = [],
}: CorrectiveActionDialogProps) {
  const [isSaving, setIsSaving] = React.useState(false);
  const [formData, setFormData] = React.useState<Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>>
    (action || {
      summary: "",
      status: "Open",
      owner: "",
      dueDate: "",
      findingId: "none",
      // DED-973: Initialize linkedDocuments
      linkedDocuments: [],
    }
  );
  const [selectedFindingId, setSelectedFindingId] = React.useState<string>("none");
  // DED-1448: Add state for audit selection
  const [selectedAuditId, setSelectedAuditId] = React.useState<string>("none");

  React.useEffect(() => {
    if (action) {
      setFormData({
        summary: action.summary,
        status: action.status,
        owner: action.owner,
        dueDate: action.dueDate || "",
        findingId: action.findingId === "none" ? "none" : action.findingId,
        auditId: action.auditId,
        // DED-973: Load linkedDocuments
        linkedDocuments: action.linkedDocuments || [],
      });
      setSelectedFindingId(action.findingId || "none");
      setSelectedAuditId(action.auditId || "none");
    } else {
      setFormData({
        summary: "",
        status: "Open",
        owner: "",
        dueDate: "",
        findingId: "none",
        // DED-973: Reset linkedDocuments
        linkedDocuments: [],
      });
      setSelectedFindingId("none");
      setSelectedAuditId("none");
    }
  }, [action, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const dataToSave = {
        ...formData,
        findingId: selectedFindingId === "none" ? "none" : selectedFindingId,
        // DED-1448: Include auditId in saved data
        auditId: selectedAuditId === "none" ? undefined : selectedAuditId,
        // DED-973: Include linkedDocuments
        linkedDocuments: formData.linkedDocuments || [],
      };
      await onSave(dataToSave);
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving corrective action:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground">
              {action ? "Edit Corrective Action" : "Add New Corrective Action"}
            </DialogTitle>
            <DialogDescription>
              {action
                ? "Update the details of this corrective action."
                : "Fill in the details for the new corrective action."}
            </DialogDescription>
          </div>
        <form id="corrective-action-form" onSubmit={handleSubmit} className="px-4 pb-4">
          <div className="grid gap-4 pt-4">
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="summary" className="text-right pt-2">
                Summary *
              </Label>
              <Textarea
                id="summary"
                value={formData.summary}
                onChange={(e) =>
                  setFormData({ ...formData, summary: e.target.value })
                }
                className="col-span-3 min-h-[80px] resize-y"
                required
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">
                Status *
              </Label>
              <Select
                value={formData.status}
                onValueChange={(value: "Open" | "Closed") =>
                  setFormData({ ...formData, status: value })
                }
                required
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="col-span-3">
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="owner" className="text-right">
                Owner *
              </Label>
              {/* <Input
                id="owner"
                value={formData.owner}
                onChange={(e) =>
                  setFormData({ ...formData, owner: e.target.value })
                }
                className="col-span-3"
                required
              /> */}
              <SingleUserCombobox
                className="col-span-3"
                selected={formData.owner}
                setSelected={(e) => setFormData({ ...formData, owner: e })}
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="dueDate" className="text-right">
                Due Date
              </Label>
              <Input
                id="dueDate"
                type="date"
                value={formData.dueDate instanceof Date ? formData.dueDate.toISOString().split('T')[0] : formData.dueDate || ""}
                onChange={(e) =>
                  setFormData({ ...formData, dueDate: e.target.value || undefined })
                }
                className="col-span-3"
              />
            </div>
            {findings.length > 0 && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="finding" className="text-right">
                  Link to Finding
                </Label>
                <Select
                  value={selectedFindingId}
                  onValueChange={setSelectedFindingId}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select a finding (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Standalone)</SelectItem>
                    {findings.map((finding) => (
                      <SelectItem key={finding.id} value={finding.id}>
                        {finding.summary.substring(0, 50)}{finding.summary.length > 50 ? '...' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* DED-1141: Removed 'Link to Audit' from corrective actions modals */}

            {/* DED-973: Linked Documents Section */}
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Documents</Label>
              <div className="col-span-3">
                <EntityLinkedDocumentsSection
                  entityType="Corrective Action"
                  linkedDocuments={formData.linkedDocuments || []}
                  onDocumentsChange={(docs) => setFormData({...formData, linkedDocuments: docs})}
                  inheritedControls={inheritedControls}
                  entityTitle={formData.summary || 'Corrective Action'}
                />
              </div>
            </div>
          </div>
        </form>
        <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="submit" form="corrective-action-form" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
